/**
 * Reusable demo device shells for the Industry Showcase carousel.
 *
 * These are data-driven versions of the hero mockups. Purely visual — no app
 * logic, no API calls, no real workspace data. Both components accept a POJO
 * `data` describing the industry's sanitized demo content.
 */
import { ShoppingCart, PackageSearch, TrendingUp, AlertTriangle } from "lucide-react";

const money = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { maximumFractionDigits: 0 });

/** POS-focused tablet mockup. `data` matches INDUSTRY_SHOWCASE[*].pos. */
export function DemoTabletPOS({ data, label }) {
  const total = (data.cart || []).reduce((s, r) => s + r.qty * r.price, 0);
  return (
    <div className="relative w-full max-w-[560px] mx-auto" data-testid={`demo-tablet-${label}`}>
      <div className="relative rounded-[26px] bg-neutral-900 shadow-2xl shadow-black/30 dark:shadow-black/60 ring-1 ring-black/10 dark:ring-white/5 p-2.5 aspect-[4/3]">
        <div className="relative h-full w-full rounded-[18px] bg-background overflow-hidden flex">
          {/* LEFT — product tiles */}
          <div className="flex-1 p-3 border-r border-border bg-muted/30 flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{data.heading}</div>
                <div className="text-[10px] font-medium">{data.menuTitle}</div>
              </div>
              <div className="text-[9px] font-mono text-muted-foreground">bizone pos</div>
            </div>
            <div className="grid grid-cols-3 gap-1.5 flex-1">
              {data.items.slice(0, 9).map((p, i) => (
                <div key={p.name}
                     className="rounded-md bg-background border border-border p-1.5 flex flex-col justify-between hover:bg-brand/5 overflow-hidden">
                  <div className={`h-5 w-5 rounded ${
                    i % 3 === 0 ? "bg-amber-500/20"
                    : i % 3 === 1 ? "bg-emerald-500/20"
                    : "bg-blue-500/20"
                  }`} />
                  <div className="mt-1 min-w-0">
                    <div className="text-[8px] leading-tight font-medium truncate">{p.name}</div>
                    <div className="text-[8px] text-muted-foreground font-mono tabular-nums">{money(p.price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* RIGHT — cart */}
          <div className="w-[42%] p-3 flex flex-col">
            <div className="flex items-center gap-1.5 mb-2">
              <ShoppingCart className="h-3 w-3 text-brand" />
              <div className="text-[10px] uppercase tracking-widest">Cart · {data.cart.length} items</div>
            </div>
            <div className="flex-1 space-y-1 overflow-hidden">
              {data.cart.map((r) => (
                <div key={r.name} className="flex items-center justify-between text-[10px]">
                  <div className="truncate pr-1">{r.name} <span className="text-muted-foreground">× {r.qty}</span></div>
                  <div className="font-mono tabular-nums">{money(r.qty * r.price)}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-border space-y-1">
              <div className="flex justify-between text-xs font-semibold">
                <span>Total</span><span className="font-mono tabular-nums">{money(total)}</span>
              </div>
              <div className="mt-1.5 flex gap-1 flex-wrap">
                {data.payments.map((m) => (
                  <div key={m}
                       className={`text-[8px] rounded px-1.5 py-1 font-medium ${
                         m === data.activePayment
                           ? "bg-brand text-brand-foreground"
                           : "bg-muted text-foreground/80"
                       }`}>
                    {m}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Secondary inventory / owner-summary panel. `data` matches INDUSTRY_SHOWCASE[*].panel. */
export function DemoInventoryPanel({ data, label }) {
  return (
    <div className="w-full max-w-sm mx-auto rounded-2xl border border-border bg-card p-4 shadow-sm"
         data-testid={`demo-inventory-${label}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{data.heading}</div>
          <div className="text-[11px] text-muted-foreground">{data.subheading}</div>
        </div>
        <div className="h-7 w-7 rounded-md bg-brand/10 grid place-items-center">
          <PackageSearch className="h-3.5 w-3.5 text-brand" />
        </div>
      </div>
      {/* KPIs */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {data.kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-border p-2">
            <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
            <div className="mt-0.5 font-display text-base tabular-nums leading-tight">{k.value}</div>
          </div>
        ))}
      </div>
      {/* Low stock */}
      <div className="mt-3">
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-muted-foreground">
          <AlertTriangle className="h-3 w-3 text-amber-600" /> Low stock
        </div>
        <ul className="mt-1.5 space-y-1">
          {data.lowStock.map((s) => (
            <li key={s.name} className="flex items-center justify-between text-[11px]">
              <span className="truncate pr-2">{s.name}</span>
              <span className="font-mono tabular-nums text-amber-700 dark:text-amber-300">{s.left}</span>
            </li>
          ))}
        </ul>
      </div>
      {/* Fast-moving */}
      <div className="mt-3">
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-muted-foreground">
          <TrendingUp className="h-3 w-3 text-emerald-600" /> Fast-moving
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {data.fastMoving.map((p) => (
            <span key={p} className="text-[10px] px-1.5 py-0.5 rounded-full border border-border bg-background">
              {p}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
